import React from 'react';

// Support both snake_case (API) and camelCase formats
interface IDPItem {
  idpId?: string;
  idpTitle?: string;
  focusArea?: string;
  status?: string;
  totalGoals?: number;
  completedGoals?: number;
  progressPercent?: number;
  // snake_case (from API)
  idp_id?: string;
  idp_title?: string;
  focus_area?: string;
  total_goals?: number;
  completed_goals?: number;
  progress_percent?: number;
}

// Helpers for snake_case/camelCase compatibility
const getIdpId = (p: IDPItem) => p.idpId || p.idp_id || '';
const getIdpTitle = (p: IDPItem) => p.idpTitle || p.idp_title || 'Individual Development Plan';
const getRawFocusArea = (p: IDPItem) => p.focusArea || p.focus_area || '';
const getStatus = (p: IDPItem) => p.status || 'Draft';
const getProgressPercent = (p: IDPItem) => Number(p.progressPercent || p.progress_percent || 0);

// Extract plain text from BlockNote JSON content
const extractBlockNoteText = (value: string): string => {
  try {
    const blocks = JSON.parse(value);
    if (!Array.isArray(blocks)) return value;
    return blocks
      .map((block: any) =>
        (block.content || [])
          .filter((c: any) => c.type === 'text')
          .map((c: any) => c.text || '')
          .join('')
      )
      .filter(Boolean)
      .join(' ');
  } catch {
    return value;
  }
};

const getFocusArea = (p: IDPItem): string => {
  const raw = getRawFocusArea(p);
  if (!raw) return '';
  return extractBlockNoteText(raw);
};

interface IDPsWidgetProps {
  idps?: IDPItem[] | { data: IDPItem[] };
  loading?: boolean;
  error?: string;
  options?: {
    showFocusArea?: boolean;
    showProgress?: boolean;
  };
}

const getStatusBadge = (status: string): { className: string } => {
  const s = status?.toLowerCase();
  if (s === 'active' || s === 'in progress') return { className: 'bg-blue-100 text-blue-700' };
  if (s === 'completed') return { className: 'bg-emerald-100 text-emerald-700' };
  return { className: 'bg-gray-100 text-gray-600' };
};

export const IDPsWidget: React.FC<IDPsWidgetProps> = ({
  idps,
  loading,
  error,
  options = {}
}) => {
  const { showFocusArea = true, showProgress = true } = options;
  const idpsList: IDPItem[] = Array.isArray(idps) ? idps : ((idps as { data: IDPItem[] })?.data || []);
  const idpCount = idpsList.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return <div className="flex items-center justify-center h-full py-8 text-red-500 text-sm">{error}</div>;
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 text-sm">IDPs</h3>
        <span className="min-w-[20px] h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-[11px] font-semibold px-1.5">
          {idpCount}
        </span>
      </div>

      {/* IDP Cards */}
      <div className="flex-1 min-h-0 space-y-3 overflow-y-auto">
        {idpsList.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">No active IDPs</div>
        ) : (
          idpsList.map((idp) => {
            const status = getStatus(idp);
            const badge = getStatusBadge(status);
            const progressPercent = getProgressPercent(idp);
            const focusArea = getFocusArea(idp);

            return (
              <div key={getIdpId(idp)} className="p-3 border border-blue-100 bg-blue-50/50 rounded-lg">
                {/* Title & Status */}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">{getIdpTitle(idp)}</span>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
                    {status}
                  </span>
                </div>

                {/* Focus Area */}
                {showFocusArea && focusArea && (
                  <div className="text-xs text-gray-500 mb-2">Focus: {focusArea}</div>
                )}

                {/* Progress Bar */}
                {showProgress && (
                  <div className="w-full h-1.5 bg-blue-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${status?.toLowerCase() === 'draft' ? 'bg-gray-300' : 'bg-blue-500'}`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default IDPsWidget;
